import { accorder } from '../plural.js';
export const en = {
    langue: {
        selecteur: 'Language',
    },
    coquille: {
        sautContenu: 'Skip to content',
        deconnexion: 'Sign out',
        versionInconnue: 'unknown',
        version: (version, horodatage) => `Version ${version} — build ${horodatage}`,
        signature: '— Repère, organised and calm',
    },
    compte: {
        menu: 'Menu',
        monCompte: 'My account',
        options: 'Options',
        aide: 'Help',
        mesDossiers: 'My case files',
        nouveauDossier: 'New case file',
        identiteTitre: 'Your account',
        formule: 'Plan',
        situation: 'Status',
        debut: 'Since',
        echeance: 'Until',
        origine: 'Source',
        origines: {
            stripe: 'Paid subscription',
            code_essai: 'Trial code',
            geste: 'Goodwill gesture',
        },
        statuts: {
            none: 'No subscription',
            trialing: 'Trial in progress',
            active: 'Active',
            past_due: 'Payment pending',
            canceled: 'Cancelled',
        },
        resiliationProgrammee: 'Your subscription will stop at the end of the current period.',
        essaiJusquau: (date) => `Free trial until ${date}.`,
        accesCouvert: 'Your writing access is open thanks to the Duo plan taken out on a case file you ' +
            'share. You can take out your own subscription at any time.',
        accesProfessionnel: 'You write under a professional access opened by a parent, not under a subscription.',
        factures: 'My invoices and payment',
        ouvertureFactures: 'Opening…',
        facturesIndisponibles: 'No invoices: your access did not come from a card payment.',
        erreurFactures: 'The billing page could not be opened',
        dossiersTitre: 'Your case files',
        parent: (rang) => `Parent ${rang}`,
        placeLibre: 'Still free',
        aucunDossier: 'You have no case file yet.',
        optionsTitre: 'Options',
        retourPrecedent: 'Back',
        apparence: 'Appearance',
        apparenceAide: '“Automatic” follows your device setting: it darkens in the evening without you thinking about it.',
        langueTitre: 'Language',
        langueAide: 'The language of the app and of its emails.',
        lectureIA: 'Receipt reading',
        lectureIAAide: 'When you attach a photo of a receipt to an expense, Repère can read it and suggest ' +
            'the amount, date and category. The photo is sent to Mistral, our European AI ' +
            'provider, for that sole purpose, and is not kept there. It is the only data from ' +
            'your case file that leaves our servers.',
        lectureIAActive: 'Read my receipts',
        lectureIAInactive: 'Send nothing, I’ll type it myself',
        notificationsTitre: 'Notifications',
        silenceAide: 'During these hours your phone stays quiet. Nothing is lost: it is held, then ' +
            'delivered all at once when the quiet period ends.',
        silenceActive: 'Keep quiet at night',
        silenceDesactive: 'Notify me at any time',
        silenceDebut: 'From',
        silenceFin: 'Until',
        silenceNote: (fin) => `Anything arriving during the quiet period reaches you at ${fin}, in a single ` +
            'message. An urgent alert still comes through.',
        silenceNoteInactif: 'You are notified as soon as something happens, at any hour.',
        emailTitre: 'Emails',
        emailAide: 'This setting concerns your inbox only. Notifications on your phone are unchanged.',
        emailImmediat: 'An email each time',
        emailQuotidien: 'A single daily summary',
        emailImmediatNote: 'You get an email as soon as something happens — except during your quiet hours.',
        emailQuotidienNote: (heure) => `One email a day, at ${heure}, with a count of what happened. Your phone still ` +
            'notifies you straight away. An urgent alert overrides this and is sent at once.',
        emailHeure: 'Summary sent at',
        conges: 'School holidays',
        congesAide: 'Where your calendar gets holiday dates from. The official calendar covers mainland ' +
            'France; an .ics file takes over for overseas territories, a school abroad, or an ' +
            'establishment that sets its own dates.',
        congesPartage: 'This choice belongs to the case file: the other parent sees the same calendar.',
        congesOfficiel: 'Official calendar',
        congesOfficielZone: (zone) => `Zone ${zone}, derived from the school’s postcode.`,
        congesOfficielSansZone: 'No zone derived: fill in the school’s postcode in the child’s record, or upload a file.',
        congesFichier: 'Uploaded file',
        congesFichierAide: 'An .ics file exported from the school’s calendar. It is read on arrival and not kept: ' +
            'only its dates are.',
        congesDeposer: 'Choose an .ics file',
        congesDepotEnCours: 'Reading the file…',
        congesDepot: (nom, periodes) => `${nom} · ${periodes} period${periodes > 1 ? 's' : ''}`,
        congesCouverture: (debut, fin) => `From ${debut} to ${fin}.`,
        congesRetirer: 'Remove this file',
        congesErreur: 'This file could not be read',
        congesAucunDossier: 'You have no case file to configure yet.',
        congesMemes: 'Children’s holidays',
        congesMemesOui: 'The same for everyone',
        congesMemesNon: 'One setting per child',
        congesMemesAide: 'A middle school and a high school on either side of an académie boundary are enough to ' +
            'split siblings. Each child then starts from the official calendar of THEIR school, and ' +
            'the calendar outlines their days in their colour.',
        congesEnfant: (prenom) => `${prenom}’s holidays`,
    },
    dates: {
        mois: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ],
        jour: 'Day',
        moisLabel: 'Month',
        annee: 'Year',
    },
    creerDossier: {
        titre: 'Create a case file',
        etapeSur4: (etape) => `Step ${etape} of 4`,
        erreurEtape: 'This step did not go through',
        jalon: { enfants: 'Children', garde: 'Custody', invitation: 'Invitation', pret: 'Ready' },
        etatJalon: { termine: 'done', enCours: 'in progress', aVenir: 'upcoming' },
        etape1: {
            titreCarte: 'Your children',
            aideFratrie: 'A case file covers the children you have with one same other parent. To add a brother or sister to an existing case file, open it and go to “Children”.',
            prenom: 'First name',
            dateNaissance: 'Date of birth',
            suffixeRang: (index) => ` for child ${index + 1}`,
            enfantParDefaut: (index) => `Child ${index + 1}`,
            retirer: 'Remove',
            retirerSuffixe: (index, prenom) => ` child ${index + 1}${prenom ? `, ${prenom}` : ''}`,
            ajouter: 'Add another child',
            continuer: 'Continue',
        },
        etape2: {
            titreCarte: 'The custody schedule',
            aideModification: "You can change this later — any change will require the other parent's agreement.",
            schema: 'Schedule',
            nidation: 'Nesting arrangement',
            nidationAide: 'The child stays in the family home, and the parents take turns living there. Time is still split according to the chosen schedule.',
            dateDebut: 'Start date',
            heure: 'Handover time',
            heureAide: 'Often when school ends.',
            quiCommence: 'Who starts',
            moi: 'Me',
            autreParent: 'The other parent',
            revenir: 'Back',
            creer: 'Create the case file',
            creationEnCours: 'Creating…',
        },
        etape3: {
            titreCarte: 'Invite the other parent',
            intro: 'Repère protects both parents equally: the other parent will have exactly the same rights as you on this case file, and neither of you can take anything away from the other.',
            redaction: 'The message is written by Repère, in a neutral tone. You have nothing to write.',
            email: "The other parent's email address",
            plusTard: 'Later',
            envoyer: 'Send the invitation',
            envoiEnCours: 'Sending…',
        },
        etape4: {
            titreCarte: 'Your case file is ready',
            messageAvecInvitation: 'The invitation has been sent. The case file is usable right away, without waiting for a reply.',
            messageSansInvitation: 'The case file is usable right away. You can invite the other parent whenever you like.',
            ouvrir: 'Open the case file',
        },
        residence: {
            pays: "Child's country of residence",
            ville: "Child's city of residence",
            zone: (zone) => `School holiday zone ${zone}.`,
            zoneInconnue: 'Zone not recognised — the exact spelling of the town helps find it.',
        },
    },
    entree: {
        presentation: {
            accrocheCourte: 'The custody schedule, messages, and documents for your children, all in one place.',
            accrocheLongue: 'Repère is the tool for separated parents: the custody schedule, messages, and documents for your children, all in one place.',
            publics: 'It is for both parents, whatever your custody arrangement and whatever the state of your relationship. A lawyer, mediator, or social worker can be invited as a read-only guest, if either of you decides to.',
            garanties: 'Everything here is timestamped by our servers, and nothing can be changed or deleted afterwards — not by you, not by the other parent, not by our team. Each of you has exactly the same rights and sees exactly the same thing.',
        },
        modesAcces: {
            titre: 'Did you receive an invitation?',
            autreParentTitre: 'You are the other parent',
            autreParentTexte: "Joining the case file is free: signing up and the calendar require no subscription. Each of you can then subscribe separately if you wish — €9.90 a month — and neither of you can cut off the other's access.",
            proTitre: 'You are a lawyer, mediator, or social worker',
            proTexte: 'Your account is free, with no limit on case files. You get read-only access to what a parent shares with you, for as long as they decide. The other parent is told of your arrival: no one enters a case file without someone knowing.',
        },
        fournisseurs: {
            indisponible: 'Coming soon',
            separateurOu: 'or',
            intro: 'With an account you already have — no new password to remember.',
            redirection: (nom) => `Redirecting to ${nom}…`,
            verbeAvec: (verbe, nom) => `${verbe} with ${nom}`,
            indisponibleDetail: 'Coming soon. The email address and password give access to the whole product: no feature will ever depend on a third-party account.',
        },
        feuille: {
            fermer: 'Close',
        },
        connexion: {
            titre: 'Sign in',
            verbe: 'Sign in',
            pasDeCompte: "Don't have an account yet?",
            sInscrire: 'Sign up',
            email: 'Email address',
            motDePasse: 'Password',
            motDePasseOublie: 'Forgot your password?',
            connexionEnCours: 'Signing in…',
            erreurTitre: 'Could not sign in',
            echecRefus: 'The sign-in was interrupted. You can try again, or enter your email and password.',
            echecExpire: 'The sign-in took too long. Please try again.',
            echecGenerique: 'The sign-in with this provider did not go through. You can enter your email and password instead.',
        },
        inscription: {
            titre: 'Create an account',
            verbe: 'Sign up',
            motDePasseTropCourt: 'The password must be at least 12 characters long.',
            verifiezBoite: 'Check your inbox',
            siAdresseUtilisable: 'If this address can be used, a message has just been sent to it. Open the link inside to confirm your address.',
            dejaConnexionPossible: 'You can already sign in: confirmation can wait.',
            dejaCompte: 'Already have an account?',
            seConnecter: 'Sign in',
            prenom: 'First name',
            prenomAide: 'Shown to the other parent.',
            email: 'Email address',
            motDePasse: 'Password',
            motDePasseAide: 'At least 12 characters. A sentence you remember makes a good password.',
            erreurTitre: 'Could not create the account',
            creationEnCours: 'Creating…',
            creerMonCompte: 'Create my account',
            echecGenerique: 'Something went wrong. Try again in a moment.',
        },
    },
    dossier: {
        categories: {
            aujourdhui: 'Today',
            calendrier: 'Calendar',
            messages: 'Messages',
            depenses: 'Expenses',
            journal: 'Log',
            enfants: 'Children',
        },
        navigation: {
            ariaCategories: 'Case file categories',
            reserveParent: 'Reserved for the Solo and Duo plans. What is already saved stays readable.',
            reserveParentSuffixe: ', reserved for the Solo and Duo plans',
            nouveaute: (n) => accorder('en', n, { one: '1 update', other: `${n} updates` }),
        },
        calendrier: {
            moisPrecedent: 'Previous month',
            moisSuivant: 'Next month',
            erreurChargement: 'The calendar could not be loaded',
            titre: (moisEtAnnee) => `Custody calendar — ${moisEtAnnee}`,
            chezVous: 'With you',
            chezAutre: (nom) => `With ${nom}`,
            nonDefini: 'not set',
            libelleEchange: 'day swap',
            libelleDerogation: 'agreed exception',
            titreEchange: 'Agreed day swap',
            titreDerogationConvenue: 'Agreed exception',
            avecMotif: (base, motif) => `${base} — ${motif}`,
            suffixeDerogation: (libelle) => `, ${libelle}`,
            suffixeMotif: (motif) => `, reason: ${motif}`,
            marqueEchange: 'Swapped',
            legendeEchange: 'Day swapped by mutual agreement',
            suffixeConge: ' — school holidays',
            suffixeCongeEnfants: (prenoms) => ` — school holidays: ${prenoms}`,
            legendeConge: (zone) => `School holidays · zone ${zone}`,
            legendeCongeFichier: 'School holidays · uploaded file',
            legendeCongeEnfant: (prenom, source) => `${prenom} — ${source}`,
            legendeCongesAbsents: 'School holidays: nothing to show. Fill in the school’s postcode in the child’s ' +
                'record, or upload a holiday file from My account › Options.',
        },
        journal: {
            ariaFiltrer: 'Filter the log',
            filtreTout: 'All',
            filtrePartagees: 'Shared',
            filtrePrivees: 'Private',
            entreeSuffixe: (n) => accorder('en', n, { one: ', 1 entry', other: `, ${n} entries` }),
            erreurChargement: 'The log could not be loaded',
            modeSoloTexte: 'You are alone on this case file for now, and the log works normally. If the other parent joins later, they will see the entries you marked as shared, with their original date.',
            videTout: 'No entries yet.',
            videPartagees: 'No shared entries.',
            videPrivees: 'No private entries.',
            auteurVous: 'You',
            auteurAutreParent: 'The other parent',
            marquePrivee: 'Private',
            marquePartagee: 'Shared',
            invisibleSuffixePrivee: 'Private entry, visible to you only, unalterable.',
            invisibleSuffixePartagee: 'Entry shared with the other parent, unalterable.',
            mentionImmuable: 'Each entry is timestamped by our servers and can no longer be changed or deleted — not by you, not by the other parent, not by our team. Note down facts: what happened, when.',
            mentionImmuableBouton: 'Log immutability',
            labelFait: 'The fact to log',
            placeholderFait: 'The fact to log — "Handover done at 6:05 pm"',
            ariaQuiPeutVoir: 'Who can see this entry?',
            intituleVisiblePar: 'Visible to',
            visibiliteAutre: 'The other parent',
            visibiliteAutreAide: 'It appears in both parents’ logs, identically.',
            visibiliteMoi: 'You only',
            visibiliteMoiAide: 'The other parent is not told. It will only appear in your own export.',
            aideChoixDefaut: 'Choose who will see this entry: nothing is pre-checked.',
            enregistrementEnCours: 'Saving…',
            consigner: 'Log it',
            erreurEnregistrement: 'The entry could not be saved',
            reformuler: 'Rephrase this text for me',
            reformulationEnCours: 'Rephrasing…',
            reformulationTitre: 'Another way to say it',
            reformulationAide: 'Nothing is kept: neither your draft nor this suggestion — no one will know ' +
                'you asked for it.',
            reformulationUtiliser: 'Use this version',
            reformulationGarder: 'Keep my text',
            reformulationDejaNeutre: 'Your text is already factual: there is nothing to soften.',
            reformulationIndisponible: 'Rephrasing is unavailable right now. You can log your text as it is.',
            reformulationBudget: 'You have used today’s rephrasings. They come back tomorrow.',
            reformulationTropLong: 'This text is too long to be rephrased.',
        },
        depenses: {
            statutAttente: 'Awaiting response',
            statutAcceptee: 'Accepted',
            statutContestee: 'Disputed',
            statutRemboursee: 'Reimbursed',
            categories: ['health', 'schooling', 'school meals', 'activities', 'clothing', 'transport'],
            comptesAJour: 'The accounts are up to date.',
            soldePhrase: (debiteur, jeSuisDebiteur, montant, crediteur) => `${debiteur} ${jeSuisDebiteur ? 'owe' : 'owes'} ${montant} to ${crediteur}.`,
            detailLigneALigne: 'The detail, line by line',
            ligneDetail: (date, categorie, montant, payeur, montantAutre) => `${date} — ${categorie}: ${montant} advanced by ${payeur}, of which ${montantAutre} is owed by the other.`,
            soldeNonStocke: 'This balance is not stored anywhere: it is recalculated on every display from these lines. You can redo the sum yourself.',
            sansRegleTitre: 'Some expenses are awaiting a split rule',
            sansRegleTexte: (n) => accorder('en', n, {
                one: '1 expense predates any rule and is not counted.',
                other: `${n} expenses predate any rule and are not counted.`,
            }),
            erreurChargement: 'The expenses could not be loaded',
            declarerUneDepense: 'Declare an expense',
            justificatifAnalyse: 'Scanned, encrypted, and attached to the case file’s documents.',
            lectureTitre: 'Read the receipt for you?',
            lectureQuestion: 'Repère can read the photo of your receipt and suggest the amount, date and ' +
                'category. You check, correct and confirm.',
            lectureDetail: 'The photo is sent to Mistral, our European AI provider, for that sole purpose. It ' +
                'is not kept there, and we keep nothing either. It is the only data from your case ' +
                'file that leaves our servers — you can withdraw this consent at any time under ' +
                '“Options”.',
            lectureAccepter: 'Yes, read my receipts',
            lectureRefuser: 'No, I’ll type it myself',
            lectureEnCours: 'Reading the receipt…',
            lectureProposee: 'Amount, date, category and comment suggested from the receipt. Please check them.',
            commentaire: 'Comment',
            commentairePlaceholder: 'Pharmacy on Main Street — antibiotics and dressings',
            commentaireAide: 'Optional. What the receipt says: the shop, what was bought. Reading the receipt can ' +
                'suggest it; you check it.',
            lectureRien: 'Nothing could be read from this photo. Enter the expense by hand.',
            lectureIndisponible: 'Reading is unavailable right now. Enter the expense by hand.',
            lectureBudget: 'You have used today’s readings. They come back tomorrow.',
            lectureQuota: 'This case file has used its five readings for today. They come back tomorrow.',
            retirer: 'Remove',
            photographierLeTicket: 'Photograph the receipt',
            choisirUnFichier: 'Choose a file',
            envoiEnCours: 'Sending…',
            justificatifAide: 'Optional, but it settles it if the expense is disputed. PDF or photo.',
            erreurJustificatif: 'The receipt could not be attached',
            montant: 'Amount',
            montantInvalide: 'Invalid amount.',
            dateDeLaDepense: 'Date of the expense',
            ariaCategorie: 'Expense category',
            erreurDeclaration: 'The expense could not be saved',
            enregistrementEnCours: 'Saving…',
            declarerLaDepense: 'Declare the expense',
            aideAvecCategorie: 'The chosen date determines the split rule applied.',
            aideSansCategorie: 'Choose a category: nothing is pre-set.',
            unParent: 'A parent',
            vous: 'You',
            justificatifJoint: 'Receipt attached',
            avancePar: (date, payeur) => `${date} · advanced by ${payeur}`,
            accepter: 'Accept',
            motifSiConteste: 'Reason, if you dispute it',
            motifAide: 'A disagreement without a reason gets nowhere: say what the problem is.',
            contester: 'Dispute',
            videListe: 'No expenses yet.',
            ongletDetail: 'Details',
            ongletPartagees: 'Shared expenses',
        },
        messagerie: {
            ariaFils: 'Conversation threads',
            filTous: 'All',
            filSansFil: 'No thread',
            filSante: 'Health',
            filEcole: 'School',
            filVacances: 'Holidays',
            filUrgences: 'Emergencies',
            messageSuffixe: (n) => accorder('en', n, { one: ', 1 message', other: `, ${n} messages` }),
            erreurChargement: 'The messages could not be loaded',
            ouvrirLaPieceJointe: 'Open the attachment',
            erreurOuverturePiece: 'The attachment could not be opened',
            pieceJointeScellee: 'Sealed with the message: replacing it would break its fingerprint.',
            redactionPieceScellee: (nomFichier) => `${nomFichier} — scanned, encrypted, and its fingerprint sealed with the message: replacing it afterwards would break the chain.`,
            videAucun: 'No messages yet.',
            videAucunDansCeFil: 'No messages in this thread.',
            nouveauxMessages: (n) => accorder('en', n, { one: '1 new message', other: `${n} new messages` }),
            vosEnvoisDifferes: 'Your scheduled sends',
            envoisDifferesAide: 'Visible to you only until they go out. You can still cancel them.',
            envoiPrevu: (corps, date, heureTexte) => `“${corps}” — scheduled for ${date} at ${heureTexte}`,
            annulerCetEnvoi: 'Cancel this send',
            retirer: 'Remove',
            mentionImmuable: 'Messages exchanged here can be neither changed nor deleted — not by you, not by the other parent, not by our team. Each one is timestamped by our servers.',
            mentionImmuableBouton: 'Message immutability',
            reformuler: 'Rephrase this message for me',
            reformulationEnCours: 'Rephrasing…',
            reformulationTitre: 'Another way to say it',
            reformulationAide: 'Nothing has been sent, and nothing is kept: neither your draft nor this ' +
                'suggestion. The other parent will not know you asked for it.',
            reformulationUtiliser: 'Use this version',
            reformulationGarder: 'Keep my text',
            reformulationDejaNeutre: 'Your message is already factual: there is nothing to soften.',
            reformulationIndisponible: 'Rephrasing is unavailable right now. Your message goes out unchanged.',
            reformulationBudget: 'You have used today’s rephrasings. They come back tomorrow.',
            reformulationTropLong: 'This message is too long to be rephrased.',
            votreMessage: 'Your message',
            placeholderEcrire: 'Write a message…',
            joindreUnDocument: 'Attach a document',
            envoyerPlusTard: 'Send later',
            envoiEnCours: 'Sending…',
            programmerLEnvoi: 'Schedule the send',
            envoyer: 'Send',
            dateEtHeureEnvoi: 'Send date and time',
            aideHorodatage: 'The timestamp of record will be that of the actual send.',
            erreurEnvoi: 'The message could not be sent',
            inactiveTexte: 'Messaging will activate once the other parent has joined the case file: it has no purpose without someone to talk to.',
            auteurVous: 'You',
            auteurAutreParent: 'The other parent',
            messageInvisible: (date, heureTexte, numero) => `${date} at ${heureTexte}. Message number ${numero}, unalterable.`,
        },
        suppression: {
            ouvrir: 'Delete this case file',
            titre: 'Delete this case file',
            texte: 'Deletion is decided together: the other parent will receive a message and must ' +
                'confirm it. Once confirmed, the case file stays readable for thirty days without ' +
                'being editable, then it is destroyed for good. Either of you can cancel at any ' +
                'point during that time.',
            texteSolo: 'You are alone on this case file: the confirmation message will be sent to you. ' +
                'Once confirmed, the case file stays readable for thirty days without being ' +
                'editable, then it is destroyed for good. You can cancel until the very last day.',
            demander: 'Request deletion',
            envoiEnCours: 'Sending…',
            erreur: 'The request could not be sent',
            renoncer: 'Do not delete',
            attenteAutre: 'Deletion of this case file has been requested. The other parent has received a ' +
                'message: it will only take effect if they confirm it.',
            attenteVous: 'Deletion of this case file has been requested. You have received a message: open ' +
                'it to confirm or refuse.',
            attenteEcheance: (date) => `Without an answer, this request cancels itself on ${date}.`,
            archiveTitre: 'Archived case file',
            archiveTexte: (date) => `This case file will be destroyed for good on ${date}.`,
            archiveLecture: 'It stays readable until then — you can re-read it and request an export — but no ' +
                'one can write to it any more.',
            annuler: 'Cancel deletion',
            annulationEnCours: 'Cancelling…',
        },
        ecran: {
            modeSolo: `You are using this case file alone`,
            modePartage: `Shared case file · identical rights`,
            modePartageAvec: (prenom) => `Shared with ${prenom} · identical rights`,
            quiALEnfant: `Who has the child?`,
            quiReponseVous: `The child is with you`,
            quiReponseAutre: (prenom) => `The child is with ${prenom}`,
            quiAucunSchema: `No custody schedule covers this date`,
            quiDerogation: `By agreed exception.`,
            quiRecherche: `Checking…`,
            quiErreur: `The answer could not be retrieved`,
            quiDateAujourdhui: (date) => `Today, ${date}`,
            quiQuelleDate: `Which date?`,
            quiAutreJour: `See another day`,
            quiRevenirAujourdhui: `Back to today`,
            demandesAccord: `Agreement requests`,
            ongletNouveautes: `What’s new`,
            ongletDossier: `The file`,
            modeSoloTexte: `The calendar, log, and documents work normally. Everything you record there is timestamped from now on.`,
            invitationEnvoyeeTexte: `An invitation has been sent. It remains valid for 30 days.`,
            renvoyerOuCorriger: `Resend or correct the address`,
            nouvelleAdresseRemplace: `The new address replaces the previous one: the link already sent stops being valid.`,
            peutInviterQuandVeut: `You can invite the other parent whenever you wish.`,
            erreurInvitation: `The invitation could not be sent`,
            emailAutreParent: `The other parent's email address`,
            aideInvitation: `The message is written by Repère, in a neutral tone.`,
            envoiEnCours: `Sending…`,
            envoyerInvitation: `Send the invitation`,
            calendrierDeGarde: `The custody calendar`,
            demanderEchange: `Request a day swap`,
            historiqueSchema: `Custody schedule history`,
            depensesPartagees: `Shared expenses`,
            ficheDe: (prenom) => `${prenom}'s profile`,
        },
    },
    schemasDeGarde: {
        schemas: {
            alternating_week: {
                libelle: 'Alternating week (7/7)',
                detail: 'One week with one, one week with the other. The most common schedule.',
            },
            two_two_five_five: {
                libelle: '2-2-5-5',
                detail: 'Two fixed days with each every week, then five days including the weekend.',
            },
            two_two_three: {
                libelle: '2-2-3',
                detail: 'Two nights, two nights, three nights: designed for younger children.',
            },
            three_four_four_three: {
                libelle: '3-4-4-3',
                detail: 'Mid-week handover, on a two-week cycle.',
            },
            fortnight: {
                libelle: 'Alternating fortnight (14/14)',
                detail: 'Two weeks with each: teenagers, or geographic distance.',
            },
            nine_five: {
                libelle: '9/5 alternation',
                detail: 'Nine nights with one, five with the other, on a two-week cycle.',
            },
            ten_four: {
                libelle: '10/4 alternation',
                detail: 'Ten nights with one, four with the other, on a two-week cycle.',
            },
            alternate_weekends: {
                libelle: 'Alternating weekends (1st, 3rd, 5th)',
                detail: 'Classic visitation and overnight right: every other weekend, Friday to Sunday.',
            },
            extended_weekends: {
                libelle: 'Extended alternating weekends',
                detail: 'Extended visitation right: every other weekend, Friday evening to Monday morning.',
            },
            visits_only: {
                libelle: 'Visits without overnight stays',
                detail: 'Reduced visitation right: daytime only, no overnight stay. Each visit is entered in the calendar.',
            },
            holidays_only: {
                libelle: 'School holidays only',
                detail: 'Long-distance visitation right: no regular weekends, most of the school holidays.',
            },
            third_party: {
                libelle: 'Residence with a third party',
                detail: "The child resides with a third party; each parent's time is set individually.",
            },
            custom: {
                libelle: 'Custom',
                detail: 'No automatic generation: the calendar is built period by period.',
            },
        },
        familles: {
            alternance: 'Alternating residence',
            alternance_inegale: 'Unequal alternating residence',
            residence_principale: 'Primary residence with one parent',
            autre: 'Other',
        },
    },
    apercu: {
        titre: 'Alternation preview',
        pasDeCycle: (libelle) => `"${libelle}" has no cycle to run: each parent's time is set period by period, in the case file's calendar. There is therefore nothing to preview here.`,
        choisirDate: 'Choose a start date: the preview will then show the first three months, day by day.',
        aPartirDu: (date) => `Starting from ${date}:`,
        joursChezVous: (n) => accorder('en', n, { one: '1 day with you', other: `${n} days with you` }),
        etJoursChezAutre: (n) => `and ${n} with the other parent, over the three months shown.`,
        chezVousInvisible: ', with you',
        chezAutreInvisible: ', with the other parent',
        legendeChezVous: 'With you',
        legendeChezAutre: 'With the other parent',
        legendePassage: 'Handover',
    },
    site: {
        nav: {
            accueil: 'Home',
            tarifs: 'Pricing',
            connexion: 'Sign in',
            inscription: 'Sign up',
        },
        footer: {
            accroche: 'Organisation and peace of mind for separated parents. Hosted in France, no tracker, no third-party cookie.',
            produit: 'Product',
            tarifsLien: 'Pricing',
            applicationsLien: 'Mobile apps',
            legal: 'Legal',
            mentionsLegales: 'Legal notice',
            confidentialite: 'Privacy policy',
            pied: (annee) => `Repère-kids — ${annee}. Hosted in France.`,
            version: (version, horodatage) => `Version ${version} — build ${horodatage}`,
        },
        accueil: {
            titre: 'Coordination for separated parents',
            description: 'Repère: custody calendar, shared expenses, timestamped messaging and an unalterable history for separated parents.',
            badgeConfiance: 'Hosted in France · No tracker',
            h1: 'Coordination, without every exchange turning into a negotiation.',
            accroche: 'A custody calendar, shared expenses, documents, exchanges — and a timestamped record that neither parent can rewrite.',
            etrePrevenu: 'Get notified at launch',
            pointChiffre: 'Encrypted per case',
            pointServeurs: 'Servers in France',
            pointHistorique: 'Unalterable history',
            question: 'Who has the kids on Wednesday?',
            reponseForte: 'Wednesday, 16 September — with you',
            reponseSuite: 'Next handover: Friday 18, 4:30 pm',
            moisCarte: 'September 2026',
            legendeChezVous: 'With you',
            legendeChezAutre: 'With the other parent',
            captureAlt: 'Screenshot of Repère-kids: the custody calendar of a shared case, August 2026.',
            captureLegende: 'The app, calendar view. Sample data.',
            surTitreChange: 'What Repère changes',
            titreChange: 'Three questions that no longer need discussing',
            introChange: 'The calendar, the agreements and the history — each settled once, by the product, not by you.',
            carte1Titre: 'A calendar that answers instantly',
            carte1Texte: 'Who has the child on Wednesday? The answer is on screen, for today as for any other date.',
            carte2Titre: 'Dated agreements',
            carte2Texte: 'A day swap is proposed, accepted or declined. Every step is recorded — nobody needs to remember who said what.',
            carte3Titre: 'A history that belongs to no one',
            carte3Texte: 'Neither you, nor the other parent, nor our support team can modify or delete what has been shared.',
            surTitreParti: 'Design principle',
            titreParti: 'Built so it can never become a weapon',
            texteParti: 'Repère records facts; it does not interpret or dramatise them. Both parents have exactly the same rights — neither can exclude the other, or remove anything from the shared history.',
            absencesIntitule: "What you won't find here",
            absence1: 'Read receipts',
            absence2: '"Online" status, "seen at", "last connection"',
            absence3: 'Lateness counter, behaviour score',
            absence4: '"Owner" account for the case',
            surTitreAcces: 'How to access',
            titreAcces: 'Did you receive an invitation?',
            introAcces: 'The question comes up before creating an account, so the answer comes before the form.',
            acces1Titre: "You're the other parent",
            acces1Texte: "Joining a case is free: signing up and the calendar require no subscription. Each parent can then subscribe if they wish — €9.90 a month — and neither can cut off the other's access.",
            acces1Badge: 'Free',
            acces2Titre: "You're a lawyer, mediator or social worker",
            acces2Texte: 'Your account is free, with no limit on cases. You get read access to what a parent opens to you, for as long as they decide. The other parent is informed of your arrival: nobody enters a case unbeknownst to anyone.',
            acces2Badge: 'Free, unlimited',
            acces3Titre: "You're exploring without having received anything",
            acces3Texte: 'Sign up for the waitlist to be notified when we launch. You only create a case if you decide to invite the other parent.',
            acces3Badge: 'No account needed',
            surTitreTarifs: 'Pricing',
            titreTarifs: 'One subscription per parent. Nothing else.',
            introTarifs: 'No "premium" tier that unlocks rights over the other parent: both parents see the same thing, whatever their subscription.',
            basculeLegende: 'Billing frequency',
            basculeMensuel: 'Monthly',
            basculeAnnuel: 'Annual',
            tarifRejoindreTitre: 'Link',
            tarifRejoindrePrix: '€0',
            tarifRejoindreTexte: 'For the parent invited to join an existing case.',
            tarifRejoindreAvantage1: 'Shared custody calendar',
            tarifRejoindreAvantage2: 'Full read access to the history',
            tarifRejoindreAvantage3: 'No time limit',
            tarifParentTitre: 'Solo',
            tarifParentEtiquette: 'Most common',
            tarifParentPrixMensuel: '€9.90 / month, per parent',
            tarifParentPrixMensuelDuo: '€14.90 / month, for both parents',
            tarifParentPrixAnnuelSeul: '€99 / year, per parent',
            tarifParentPrixAnnuelDuo: '€149 / year, for both parents',
            tarifParentPrixAnnuel: 'Two months free compared to monthly.',
            tarifParentEssaiGratuit: '7-day free trial',
            tarifParentBoutonNotification: 'Reserve my Early bird access',
            tarifParentSwitchDuo: 'Include the other parent',
            tarifParentEarlybirdEtiquette: 'Early bird',
            tarifParentEarlybirdTexte: '€79 for the first year for waitlist subscribers, then the annual rate.',
            tarifParentEarlybirdTexteDuo: '€129 for the first year for waitlist subscribers, then the annual rate.',
            tarifParentEngagement: 'No monthly commitment. Cancel in one click from your account.',
            tarifParentOptionDeuxEtiquette: 'Duo',
            tarifParentOptionDeuxTexte: "+€5 a month, €14.90 in total, to cover the other parent's subscription too.",
            tarifParentOptionDeuxTexteAnnuel: "+€50 a year, €149 in total, to cover the other parent's subscription too.",
            tarifParentAvantage1: 'Everything in Link',
            tarifParentAvantage2: 'Messaging, shared expenses, encrypted documents',
            tarifParentAvantage3: 'Swap proposals and journal',
            tarifParentAvantage4: 'Timestamped case export',
            tarifParentAvantage5: 'Invite a professional in read-only mode',
            tarifProTitre: 'Pro',
            tarifProPrix: '€0',
            tarifProTexte: 'Lawyer, family mediator, social worker.',
            tarifProAvantage1: 'Unlimited number of cases',
            tarifProAvantage2: "Read access, on a parent's invitation",
            tarifProAvantage3: 'Authenticated history export',
            mentionTarifs: 'Prices include tax. The service is not yet open: exact terms — trial period, cancellation terms — will be published here at launch.',
            surTitreMobile: 'On your phone',
            titreMobile: 'The calendar in your pocket, the history in the same place',
            texteMobile: 'The iOS and Android apps give access to the same case as the web: same rights, same data, same history. A notification tells you about a swap proposal or an urgent alert — never about whether the other parent is online.',
            appStore: 'App Store',
            googlePlay: 'Google Play',
            mobileNote: 'The App Store and Google Play links will go live when the service opens.',
            mobileCalendrierAlt: "Preview of the mobile app's calendar screen.",
            mobileDossiersAlt: "Preview of the mobile app's home screen: the list of cases.",
            mobileLegende: 'Previews of the mobile app. Sample data.',
        },
        waitlist: {
            titre: 'Get notified at launch',
            texte: "Leave your address: you'll get a message when we launch, and nothing else. No newsletter, no follow-up.",
            labelEmail: 'Email address',
            bouton: 'Sign me up',
            aide: 'Used only to notify you at launch. Removable at any time.',
            erreurEmailInvalide: 'Please enter a valid email address.',
            envoiEnCours: 'Sending…',
            succes: "Got it. You'll receive a message at launch.",
            echec: 'The sign-up failed. Please try again shortly.',
        },
        confidentialite: {
            titre: 'Privacy policy',
            description: 'How Repère handles personal data: hosted in France, encryption, no trackers.',
            avisProvisoire: 'Draft document, pending legal review. It faithfully describes how the service works technically, but its legal wording must be reviewed by counsel before launch (see the project roadmap).',
            miseAJour: (date) => `Last updated: ${date}.`,
            siteTitre: 'This site',
            siteTexte1: 'The site does not set any cookies and does not call any third-party service.',
            siteTexte2: 'If you sign up for the waitlist, only your email address is kept, along with the page the sign-up came from. It is used solely to notify you at launch, and you can ask for it to be deleted at any time.',
            serviceTitre: 'The service',
            serviceHebergementTitre: 'Hosted in France',
            serviceHebergementTexte: ', including backups. No transfer outside the European Union.',
            serviceChiffrementTitre: 'Encryption',
            serviceChiffrementTexte: ': exchanges are encrypted in transit; sensitive content and documents are encrypted at rest, with a key specific to each co-parenting case.',
            serviceBoutTitre: 'No end-to-end encryption',
            serviceBoutTexte: ', and we say so clearly: the service needs to be able to timestamp, seal and export the history to keep its promise. Your content can be decrypted by the service, never by a third party.',
            serviceHistoriqueTitre: 'Shared history retained',
            serviceHistoriqueTexte: ': deleting an account anonymises its holder but does not destroy the shared history, which also belongs to the other parent. This is an essential feature of the service, disclosed at sign-up.',
            serviceSignatureTitre: 'Independent proof',
            serviceSignatureTexte: ': each entry is digitally signed the moment it is recorded, by an independent party outside Repère — the same approach used to certify an official document. The proof that nothing has changed since stays verifiable at any time.',
            servicePubTitre: 'No advertising, no tracker, no resale.',
            servicePubTexte: ' Technical logs do not contain personal data.',
            droitsTitre: 'Your rights',
            droitsTexte: "Access, rectification, portability, erasure, restriction and objection can be exercised with the service. Exporting the case also serves as a response to an access request. The data protection officer's contact details will be published here before the service launches.",
        },
        mentionsLegales: {
            titre: 'Legal notice',
            description: 'Legal notice for the Repère website: publisher, hosting and intellectual property.',
            avisProvisoire: "Draft document, pending legal review. The publisher's and host's identification details will be completed before the service launches.",
            miseAJour: (date) => `Last updated: ${date}.`,
            editeurTitre: 'Publisher',
            editeurTexte: 'Repère — legal form, registered office, registration number and publication director to be completed.',
            hebergementTitre: 'Hosting',
            hebergementTexte: 'The site and the service are hosted in France. No data is transferred outside the European Union.',
            contactTitre: 'Contact',
            contactTexte: 'Contact address to be completed.',
            proprieteTitre: 'Intellectual property',
            proprieteTexte: 'The Repère brand, its logo and the content of this site are protected. Any reproduction without authorisation is prohibited.',
        },
    },
    notifications: {
        libelles: {
            message_new: { un: 'a new message', plusieurs: (n) => `${n} new messages` },
            proposal_new: {
                un: 'a new agreement request',
                plusieurs: (n) => `${n} new agreement requests`,
            },
            proposal_answered: {
                un: 'a reply to an agreement request',
                plusieurs: (n) => `${n} replies to agreement requests`,
            },
            expense_new: { un: 'a new expense', plusieurs: (n) => `${n} new expenses` },
            document_new: { un: 'a new document', plusieurs: (n) => `${n} new documents` },
            calendar_event_new: {
                un: 'a new calendar entry',
                plusieurs: (n) => `${n} new calendar entries`,
            },
            alert_new: { un: 'an alert', plusieurs: (n) => `${n} alerts` },
            professional_invited: {
                un: 'a professional access opened',
                plusieurs: (n) => `${n} professional accesses opened`,
            },
            writing_opened: {
                un: 'writing opened on a case',
                plusieurs: (n) => `writing opened on ${n} cases`,
            },
        },
        enumeration: (morceaux) => morceaux.length <= 1
            ? (morceaux[0] ?? '')
            : `${morceaux.slice(0, -1).join(', ')} and ${morceaux.at(-1)}`,
    },
    mail: {
        verification: {
            objet: 'Confirm your email address',
            texte: (lien) => [
                'Hello,',
                '',
                'To confirm your email address on Repère, open this link:',
                lien,
                '',
                'This link is valid for 24 hours. If you did not request this,',
                'you can ignore this message: no account will be activated.',
                '',
                'Repère — organisation & peace of mind',
            ].join('\n'),
            titre: 'Confirm your email address',
            paragraphes: ['Hello,', 'To confirm your email address on Repère, use the button below.'],
            bouton: 'Confirm my address',
            note: "This link is valid for 24 hours. If you didn't request this, you can ignore this message: no account will be activated.",
        },
        invitationDossier: {
            objet: 'You have been invited to join a case on Repère',
            texte: (inviterName, lien) => [
                'Hello,',
                '',
                `${inviterName} uses Repère to organise your children's daily life`,
                'and invites you to join the case.',
                '',
                "Repère protects both parents the same way: you'll have exactly the",
                'same rights, and the history belongs to neither. Nobody can',
                'remove anything from it, or exclude you.',
                '',
                'To join the case:',
                lien,
                '',
                "This link is valid for 30 days. If you'd rather not use it, you can",
                'ignore this message: nothing will happen.',
                '',
                'Repère — organisation & peace of mind',
            ].join('\n'),
            titre: 'You have been invited to join a case',
            paragraphes: (inviterName) => [
                'Hello,',
                `${inviterName} uses Repère to organise your children's daily life and invites you to join the case.`,
                "Repère protects both parents the same way: you'll have exactly the same rights, and the history belongs to neither. Nobody can remove anything from it, or exclude you.",
            ],
            bouton: 'Join the case',
            note: "This link is valid for 30 days. If you'd rather not use it, you can ignore this message: nothing will happen.",
        },
        digest: {
            objet: "There's news in your Repère case",
            texte: (resume, webUrl) => [
                'Hello,',
                '',
                `Your Repère case has ${resume}.`,
                '',
                'To view it:',
                webUrl,
                '',
                'You can adjust your quiet hours and the frequency of these messages',
                'in your account preferences.',
                '',
                'Repère — organisation & peace of mind',
            ].join('\n'),
            titre: 'There is news in your case',
            paragraphes: (resume) => ['Hello,', `Your Repère case has ${resume}.`],
            bouton: 'Open my case',
            note: 'You can adjust your quiet hours and the frequency of these messages in your account preferences.',
        },
        alerte: {
            objetUrgent: 'Urgent alert on your Repère case',
            objetNormal: 'Alert on your Repère case',
            texte: (motif, webUrl) => [
                'Hello,',
                '',
                `An alert was logged in your Repère case, with the reason "${motif}".`,
                '',
                'To read it:',
                webUrl,
                '',
                'Repère — organisation & peace of mind',
            ].join('\n'),
            titreUrgent: 'Urgent alert on your case',
            titreNormal: 'Alert on your case',
            paragraphes: ['Hello,', 'An alert was logged in your Repère case.'],
            encart: (motif) => `Reason given: "${motif}"`,
            bouton: 'Read the alert',
        },
        invitationPro: {
            objet: 'A professional access has been opened for you on Repère',
            texte: (modules, url) => [
                'Hello,',
                '',
                'A parent has opened read-only access to their Repère case for you.',
                '',
                `You will see: ${modules}.`,
                '',
                "This access is read-only: you don't write anything, and nothing you",
                'view is changed by your visit. Your visits are logged in the',
                "case's register, which both parents can read.",
                'Either parent can end it at any time.',
                '',
                'It is free and with no commitment. To find it:',
                url,
                '',
                'Repère — organisation & peace of mind',
            ].join('\n'),
            titre: 'A professional access has been opened for you',
            paragraphes: (modules) => [
                'Hello,',
                'A parent has opened read-only access to their Repère case for you.',
                `You will see: ${modules}.`,
                "This access is read-only: you don't write anything, and nothing you view is changed by your visit. Your visits are logged in the case's register, which both parents can read. Either parent can end it at any time.",
                'It is free and with no commitment.',
            ],
            bouton: 'Open my access',
        },
        lienPaiement: {
            objet: 'Your link to subscribe to Repère',
            texte: (lien) => [
                'Hello,',
                '',
                'You just asked to subscribe from the Repère app.',
                'Open this link to finish, on this device or another one:',
                lien,
                '',
                'This link is valid for 1 hour. After that, request a new one from the app.',
                'Nothing is charged until you confirm.',
                '',
                '— Repère',
            ].join('\n'),
            titre: 'Finish your subscription',
            paragraphes: [
                'You just asked to subscribe from the app. The payment finishes here, on this device or another one.',
                'Nothing is charged until you confirm.',
            ],
            bouton: 'Finish payment',
            note: 'This link is valid for 1 hour. After that, request a new one from the app.',
        },
        reinitialisation: {
            objet: 'Password reset',
            texte: (lien) => [
                'Hello,',
                '',
                'You requested a password reset for your Repère account. Open this link:',
                lien,
                '',
                'This link is valid for 1 hour and only works once.',
                "If you didn't request this, ignore this message:",
                'your current password remains valid.',
                '',
                'Repère — organisation & peace of mind',
            ].join('\n'),
            titre: 'Password reset',
            paragraphes: ['Hello,', 'You requested a password reset for your Repère account.'],
            bouton: 'Reset my password',
            note: "This link is valid for 1 hour and only works once. If you didn't request this, ignore this message: your current password remains valid.",
        },
        suppressionDossier: {
            objet: 'A case file deletion is waiting for your confirmation',
            texte: (demandeur, lien) => [
                'Hello,',
                '',
                demandeur
                    ? `${demandeur} has requested the deletion of a Repère case file you share.`
                    : 'You have requested the deletion of one of your Repère case files.',
                '',
                'Nothing has been deleted yet. The deletion is confirmed from this link:',
                lien,
                '',
                'Once confirmed, the case file stays readable for thirty days without being',
                'editable, then it is destroyed for good. During that time the deletion can be',
                'cancelled at any moment, by either of you.',
                '',
                'This link is valid for 7 days. Without an answer, the request cancels itself and',
                'the case file stays intact.',
                '',
                'Repère — organisation & serenity',
            ].join('\n'),
            titre: 'A case file deletion is waiting for your confirmation',
            paragraphes: (demandeur) => [
                'Hello,',
                demandeur
                    ? `${demandeur} has requested the deletion of a Repère case file you share.`
                    : 'You have requested the deletion of one of your Repère case files.',
                'Nothing has been deleted yet. Once the deletion is confirmed, the case file stays readable for thirty days without being editable, then it is destroyed for good. During that time it can be cancelled at any moment, by either of you.',
            ],
            bouton: 'Open the request',
            note: 'This link is valid for 7 days. Without an answer, the request cancels itself and the case file stays intact.',
        },
    },
    mobile: {
        notifications: {
            verification: 'Notifications: checking…',
            pretes: 'Notifications ready on this device.',
            refusees: 'You declined notifications. You can turn them back on in your phone settings.',
            indisponiblesTitre: 'Notifications unavailable',
            appareilRattacheTitre: 'Device registered',
            appareilActif: 'This device is registered. You will be notified even when the app is closed.',
            appareilInactif: 'You are signed in, but this device cannot be notified yet.',
        },
        dossiers: {
            titre: 'Your case files',
            chargement: 'Loading…',
            erreur: 'Could not load your case files.',
            aucun: 'No case file yet. You can create one from the web app.',
            deconnexion: 'Sign out',
            ouvrirIndice: 'Opens this case file’s calendar',
        },
        calendrier: {
            retour: 'Back',
            aujourdHui: 'Today',
            jusqua: (heure) => `Until ${heure}`,
        },
    },
};
//# sourceMappingURL=en.js.map